import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../providers/AuthContext';
import { CategoryContext } from '../../providers/CategoryContext';
import { ConfigContext } from '../../providers/ConfigContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const Menu = ({ isAdminAuthenticated }) => {
  const { user, logout } = useContext(AuthContext);
  const { categories } = useContext(CategoryContext);
  const { config } = useContext(ConfigContext);
  const cartItems = useSelector(state => state.cart.items);
  const totalItems = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [announcement, setAnnouncement] = useState({
    enabled: true,
    messages: [
      'Envio gratis en compras superiores a $3.000',
      'Cambios y devoluciones faciles'
    ]
  });
  const [instagramUrl, setInstagramUrl] = useState('');

  const firstName = user?.displayName?.split(' ')[0] || 'Usuario';
  const announcementMessages = useMemo(
    () => announcement.messages.filter(message => String(message || '').trim()),
    [announcement.messages]
  );
  const tickerMessages = announcementMessages.length > 0
    ? Array.from({ length: 8 }, () => announcementMessages).flat()
    : [];

  useEffect(() => {
    let isMounted = true;

    const loadAnnouncement = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/home-content`);
        const data = await response.json();

        if (!isMounted || !response.ok) {
          return;
        }

        if (data.data?.announcement) {
          setAnnouncement({
            enabled: data.data.announcement.enabled !== false,
            messages: Array.isArray(data.data.announcement.messages)
              ? data.data.announcement.messages
              : []
          });
        }

        if (Array.isArray(data.data?.socialLinks)) {
          const ig = data.data.socialLinks.find(l => l.platform === 'instagram' && l.enabled && l.url);
          if (ig) setInstagramUrl(ig.url);
        }
      } catch (error) {
        console.error('Error cargando mensajes del header:', error);
      }
    };

    loadAnnouncement();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {announcement.enabled && tickerMessages.length > 0 && (
        <div className="menu-announcement" aria-label="Anuncios de la tienda">
          <div className="menu-announcement-track">
            {tickerMessages.map((message, index) => (
              <span className="menu-announcement-message" key={`${message}-${index}`}>
                {message}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ───── Main Header ───── */}
      <nav
        id="barramenu"
        className="enhanced-header menu-header"
      >
        {/* Left: Hamburger (mobile) + Nav */}
        <div className="menu-nav-area">
          {/* Hamburger for mobile */}
          <button
            className={`modern-hamburger d-md-none ${isMobileMenuOpen ? 'is-active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Menu"
            style={{ marginRight: '12px' }}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* Desktop nav links */}
          <div className="desktop-menu">
            <Link to="/tienda" className="nav-link">Tienda</Link>

            {categories.slice(0, 3).map(category => (
              <div
                key={category._id}
                className="nav-dropdown"
                style={{ position: 'relative' }}
                onMouseEnter={() => setActiveDropdown(category._id)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={`/tienda?category=${encodeURIComponent(category.name)}`}
                  className="nav-link nav-link-dropdown"
                >
                  {category.name}
                  {category.subcategories?.length > 0 && (
                    <i className="fa-solid fa-chevron-down ms-1" style={{
                      fontSize: '8px',
                      transition: 'transform 0.2s',
                      transform: activeDropdown === category._id ? 'rotate(180deg)' : 'rotate(0)'
                    }}></i>
                  )}
                </Link>

                {/* Dropdown for subcategories */}
                {activeDropdown === category._id && category.subcategories?.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '-20px',
                    background: 'transparent',
                    minWidth: '260px',
                    paddingTop: '8px',
                    zIndex: 1001
                  }}>
                    <div style={{
                      background: '#ffffff',
                      border: '1px solid var(--border-light)',
                      padding: '16px 0',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                      borderRadius: '4px'
                    }}>
                    {category.subcategories.map(sub => (
                      <Link
                        key={sub._id || sub.name}
                        to={`/tienda?category=${encodeURIComponent(category.name)}&subcategory=${encodeURIComponent(sub.name)}`}
                        style={{
                          display: 'block',
                          padding: '10px 24px',
                          color: 'var(--text-primary)',
                          fontSize: '0.8rem',
                          fontWeight: 400,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          textDecoration: 'none',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--bg-secondary)';
                          e.target.style.color = 'var(--brand-secondary)';
                          e.target.style.paddingLeft = '28px';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = 'var(--text-primary)';
                          e.target.style.paddingLeft = '24px';
                        }}
                      >
                        {sub.name}
                      </Link>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="nav-link">Instagram</a>
            )}
            <Link to="/contacto" className="nav-link">Contacto</Link>
          </div>
        </div>

        {/* Center: Logo / Brand */}
        <Link to="/" className="brand-link" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {config?.brand?.logo ? (
            <img 
              src={getDisplayImageUrl(config.brand.logo)} 
              alt={config?.brand?.name || 'Tienda'} 
              style={{ maxHeight: '60px', maxWidth: '220px', objectFit: 'contain' }} 
            />
          ) : (
            <span className="brand-wordmark">
              {config?.brand?.name || 'E-MOREIRA'}
            </span>
          )}
        </Link>

        {/* Right: Icons */}
        <div className="menu-actions">
          {/* User */}
          {user ? (
            <>
              <Link
                to="/profile"
                className="nav-link nav-icon-link"
                title={firstName}
              >
                <i className="fa-solid fa-user"></i>
              </Link>
              <button
                onClick={() => { logout(); navigate('/user'); }}
                className="nav-icon-button"
                title="Cerrar sesión"
              >
                <i className="fa-solid fa-sign-out-alt"></i>
              </button>
            </>
          ) : (
            <Link to="/user" className="nav-link nav-icon-link">
              <i className="fa-regular fa-user"></i>
            </Link>
          )}

          {/* Cart */}
          <Link
            to="/cart"
            className="nav-link nav-icon-link cart-link"
          >
            <i className="fa-solid fa-bag-shopping"></i>
            {totalItems > 0 && (
              <span className="menu-cart-count">
                {totalItems}
              </span>
            )}
          </Link>

          {/* Admin shortcut */}
          {isAdminAuthenticated && (
            <Link to="/dashboard" className="nav-link nav-icon-link d-none d-md-block">
              <i className="fa-solid fa-chart-line"></i>
            </Link>
          )}
        </div>
      </nav>

      {/* ───── Mobile Sidebar ───── */}
      {isMobileMenuOpen && (
        <div className="mobile-sidebar" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '300px',
          height: '100vh',
          background: '#ffffff',
          zIndex: 1010,
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out',
          padding: '30px 24px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            {config?.brand?.logo ? (
              <img 
                src={getDisplayImageUrl(config.brand.logo)} 
                alt={config?.brand?.name || 'Tienda'} 
                style={{ maxHeight: '30px', maxWidth: '150px', objectFit: 'contain' }} 
              />
            ) : (
              <span style={{
                fontFamily: 'var(--font-accent)',
                fontSize: '1.2rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase'
              }}>
                {config?.brand?.name || 'E-MOREIRA'}
              </span>
            )}
            <button
              onClick={toggleMobileMenu}
              style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div className="mobile-nav-links">
            <Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Inicio
            </Link>
            <Link to="/tienda" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Tienda
            </Link>

            {categories.map(category => (
              <Link
                key={category._id}
                to={`/tienda?category=${encodeURIComponent(category.name)}`}
                className="mobile-nav-link"
                onClick={toggleMobileMenu}
              >
                {category.name}
              </Link>
            ))}

            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="mobile-nav-link" onClick={toggleMobileMenu}>
                Instagram
              </a>
            )}
            <Link to="/contacto" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Contacto
            </Link>
            <Link to="/cart" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Carrito {totalItems > 0 && `(${totalItems})`}
            </Link>

            <div style={{ borderTop: '1px solid var(--border-light)', marginTop: '16px', paddingTop: '16px' }}>
              {user ? (
                <>
                  <Link to="/profile" className="mobile-nav-link" onClick={toggleMobileMenu}>
                    Mi Cuenta
                  </Link>
                  <button
                    onClick={() => { logout(); navigate('/user'); toggleMobileMenu(); }}
                    className="mobile-nav-link"
                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                  >
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <Link to="/user" className="mobile-nav-link" onClick={toggleMobileMenu}>
                  Iniciar sesión
                </Link>
              )}
              {isAdminAuthenticated && (
                <Link to="/dashboard" className="mobile-nav-link" onClick={toggleMobileMenu}>
                  Dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={toggleMobileMenu}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1009
          }}
        />
      )}
    </>
  );
};

export default Menu;
